import Footer from '@/components/footer';
import Header from '@/components/header';
import Headc from '@/ctf-components/headc/headc';
import SlugPage from '@/ctf-components/slug-page';
import { fetchFooterData, fetchHeaderMainMenuNavigation } from '@/hooks';
import {
  fetchExpertiseArticle,
  fetchExpertiseContentArticle,
  queryExpertiseArticles,
  useExpertiseArticle,
  useExpertiseContentArticle
} from '@/hooks/useExpertiseArticle';
import { fetchConfig } from '@/lib/fetchConfig';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { useRouter } from 'next/router';

const ExpertiseArticles = () => {
  const { locale, query } = useRouter();
  const slug0 = query.slug?.[0] || '';
  const slug1 = query.slug?.[1] || '';
  const { data, isLoading } = useExpertiseArticle(
    locale,
    'Expertise',
    slug0,
    slug1
  );

  const { data: contentData, isLoading: isContentLoading } =
    useExpertiseContentArticle(locale, 'Expertise', slug0, slug1);

  if (isLoading || isContentLoading) return <div>Loading</div>;
  const {
    articleCollection: {
      items: [story],
      items: [{ seo }]
    }
  } = data;

  const {
    articleCollection: {
      items: [contentStory]
    }
  } = contentData;

  return (
    <>
      <Headc seo={seo} />
      <Header />
      {data && contentStory && (
        <SlugPage data={story} contentStory={contentStory} />
      )}
      <Footer />
    </>
  );
};
export async function getStaticPaths({
  locales,
  params
}: {
  locales: any;
  params: any;
}) {
  const variables = {
    category: 'Expertise'
  };
  const res = await fetch(fetchConfig.endpoint as string, {
    method: 'POST',
    ...fetchConfig.params,
    body: JSON.stringify({ query: queryExpertiseArticles, variables })
  });

  const json = await res.json();
  if (json.errors) {
    const { message } = json.errors[0];
    throw new Error(message);
  }
  const {
    articleCollection: { items: stories }
  } = json.data;

  const paths = stories.map((slugItem: any) => ({
    params: { slug: [slugItem.subCategory, slugItem.slug] }
  }));

  return {
    paths: [
      ...paths,
      ...paths.flatMap((path: any) =>
        locales.map((locale: any) => ({
          ...path,
          locale
        }))
      )
    ],
    fallback: 'blocking'
  };
}

export async function getStaticProps({ params, locale }: any) {
  const queryClient = new QueryClient();

  await queryClient.prefetchQuery({
    queryKey: [params.slug[1], locale],
    queryFn: () =>
      fetchExpertiseArticle(locale, 'Expertise', params.slug[0], params.slug[1])
  });

  await queryClient.prefetchQuery({
    queryKey: [params.slug[0], params.slug[1], locale],
    queryFn: () =>
      fetchExpertiseContentArticle(
        locale,
        'Expertise',
        params.slug[0],
        params.slug[1]
      )
  });

  const articleData: any = queryClient.getQueryData([params.slug[1], locale]);
  const articleContentData: any = queryClient.getQueryData([
    params.slug[0],
    params.slug[1],
    locale
  ]);

  if (
    !articleContentData ||
    !articleContentData.articleCollection?.items.length
  ) {
    return {
      notFound: true
    };
  }

  if (!articleData || !articleData.articleCollection?.items.length) {
    return {
      notFound: true
    };
  }

  await queryClient.prefetchQuery({
    queryKey: ['footer', locale],
    queryFn: () => fetchFooterData(locale)
  });

  await queryClient.prefetchQuery({
    queryKey: ['headermainmenu', locale],
    queryFn: () => fetchHeaderMainMenuNavigation(locale)
  });

  return {
    props: {
      dehydratedState: dehydrate(queryClient)
    },
    revalidate: 10
  };
}

export default ExpertiseArticles;
